import React, { useState, useRef, useEffect, useCallback } from 'react';
import experiencesData from '../../data/experience.json';
import type { ExperienceItem, ExperienceProps } from './experience.types';
import './Experience.styles.css';

const experiences: readonly ExperienceItem[] = experiencesData as ExperienceItem[];

// * Formats period strings into concise Month-Year markers (e.g., "April 2025 – Present" -> "Apr 2025")
const formatTimelineDate = (period: string): string => {
  try {
    const startPart = period.split('–')[0]?.trim() || period.split('-')[0]?.trim() || period;
    const match = startPart.match(/([A-Za-z]+)\s+(\d{4})/);
    if (match) {
      const month = match[1].slice(0, 3);
      const year = match[2];
      return `${month} ${year}`;
    }
    return startPart;
  } catch (err) {
    console.error('[Experience] Date formatting error:', err);
    return period;
  }
};

// * [COMPONENT] Professional Experience section with inverted horizontal bottom timeline & HUD styling
// ? Desktop uses a center-right biased card + right-to-left timeline; mobile uses touch swipe + compact stepper
export const Experience: React.FC<ExperienceProps> = ({
  className = '',
  scale = 1,
}) => {
  // * Default to index 0 (latest, left-most on timeline)
  const [activeTab, setActiveTab] = useState<number>(0);
  const scrollableBodyRef = useRef<HTMLDivElement | null>(null);

  // * Screen width tracking for scale transform origin
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // * Mobile touch swipe detection coordinates
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  // * Switch active experience role
  const handleSelectRole = useCallback((idx: number): void => {
    // ! Guard against out-of-bounds selection
    if (idx < 0 || idx >= experiences.length) return;
    setActiveTab(idx);

    // * Reset internal scroll to top when switching roles
    if (scrollableBodyRef.current) {
      scrollableBodyRef.current.scrollTop = 0;
    }
  }, []);

  // * Mobile touch swipe event handlers
  const handleTouchStart = (e: React.TouchEvent): void => {
    try {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    } catch (err) {
      console.error('[Experience] Touch start error:', err);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent): void => {
    try {
      if (touchStartX.current === null || touchStartY.current === null) return;
      const deltaX = e.changedTouches[0].clientX - touchStartX.current;
      const deltaY = e.changedTouches[0].clientY - touchStartY.current;
      touchStartX.current = null;
      touchStartY.current = null;

      // ? Detect intentional horizontal swipe with vertical scroll tolerance
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 40) {
        if (deltaX < 0 && activeTab < experiences.length - 1) {
          // Swipe left -> next role
          handleSelectRole(activeTab + 1);
        } else if (deltaX > 0 && activeTab > 0) {
          // Swipe right -> previous role
          handleSelectRole(activeTab - 1);
        }
      }
    } catch (err) {
      console.error('[Experience] Touch end error:', err);
    }
  };

  // * Keyboard Arrow navigation (Left/Right to step timeline)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      // Avoid intercepting when user is in form inputs
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) return;

      if (e.key === 'ArrowRight' && activeTab < experiences.length - 1) {
        handleSelectRole(activeTab + 1);
      } else if (e.key === 'ArrowLeft' && activeTab > 0) {
        handleSelectRole(activeTab - 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, handleSelectRole]);

  const activeExp = experiences[activeTab] || experiences[0];

  // * Inverted progress calculation (career advances from right/Jun 2019 to left/Apr 2025)
  const invertedProgressPercent =
    experiences.length > 1
      ? ((experiences.length - 1 - activeTab) / (experiences.length - 1)) * 100
      : 0;

  return (
    <section className={`experience-section ${className}`}>
      <div
        className="experience-container"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: isMobile ? 'top center' : 'center center',
          transition: 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* * Upper Presentation Stage: Dynamic Role Showcase Card (Biased Center-Right) */}
        <div
          className="experience-showcase-stage fp-animate"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div key={activeTab} className="experience-card">
            {/* * Pinned Card Header: Anchors role metadata without scrolling */}
            <div className="experience-card-pinned-header">
              {/* * Category / Pretag Identifier styled like About section */}
              <div className="experience-top-bar">
                <span className="experience-pretag">
                  <span className="experience-pretag-dot" />
                  // Career Timeline
                </span>
                <span className="period-pill">{activeExp.period}</span>
              </div>

              <div className="role-main">
                <h3 className="position-title">{activeExp.position}</h3>
                <div className="company-badge">
                  <span className="company-title">{activeExp.company}</span>
                  <span className="location-badge">📍 {activeExp.location}</span>
                </div>
              </div>
            </div>

            {/* * Scrollable Body: Internal scroll strictly for job description & tech skills */}
            <div
              ref={scrollableBodyRef}
              className="experience-card-scrollable custom-scrollbar"
              data-testid="experience-scrollable-body"
            >
              <div className="role-achievements">
                <span className="experience-section-heading">Key Contributions & System Impact</span>
                <ul className="achievements-list">
                  {activeExp.achievements.map((achievement, idx) => (
                    <li key={idx} className="achievement-item">
                      <span className="bullet-arrow">⚡</span>
                      <span className="achievement-text">{achievement}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="tech-section">
                <span className="experience-section-heading">Technologies & Execution Tooling</span>
                <div className="tech-chips">
                  {activeExp.tech.map((tech, idx) => (
                    <span key={idx} className="tech-chip">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* * Desktop Bottom Horizontal Timeline (Inverted Progress Right-to-Left, Centered) */}
        <nav
          className="experience-timeline fp-animate"
          aria-label="Career Timeline"
        >
          <div className="timeline-track">
            {/* Base line */}
            <div className="timeline-line-background" />
            {/* Inverted Active filled line (fills from right to left) */}
            <div
              className="timeline-line-fill"
              style={{ width: `${invertedProgressPercent}%` }}
            />
            {/* Timeline Nodes */}
            <div className="timeline-nodes">
              {experiences.map((exp, index) => {
                const isActive = activeTab === index;
                // Career progress flows right to left; nodes from career start (right) up to activeTab are passed
                const isPassed = index >= activeTab;
                const dateLabel = formatTimelineDate(exp.period);

                return (
                  <button
                    key={index}
                    type="button"
                    className={`timeline-node ${isActive ? 'active' : ''} ${isPassed ? 'passed' : ''}`}
                    onClick={() => handleSelectRole(index)}
                    aria-label={`View role at ${exp.company} (${dateLabel})`}
                    aria-current={isActive ? 'true' : 'false'}
                  >
                    <span className="node-dot-outer">
                      <span className="node-dot-inner" />
                    </span>
                    <span className="node-label">{dateLabel}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </nav>

        {/* * Mobile View (Option 2): Gesture Swipe + Compact Bottom Stepper */}
        <div className="mobile-stepper-bar fp-animate">
          <button
            type="button"
            className="mobile-stepper-btn"
            onClick={() => handleSelectRole(activeTab - 1)}
            disabled={activeTab === 0}
            aria-label="Previous role"
          >
            ‹ Prev
          </button>

          <div className="mobile-stepper-center">
            <div className="mobile-stepper-dots">
              {experiences.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`mobile-dot ${activeTab === idx ? 'active' : ''}`}
                  onClick={() => handleSelectRole(idx)}
                  aria-label={`Jump to role ${idx + 1}`}
                />
              ))}
            </div>
            <span className="mobile-active-date">
              {formatTimelineDate(activeExp.period)}
            </span>
          </div>

          <button
            type="button"
            className="mobile-stepper-btn"
            onClick={() => handleSelectRole(activeTab + 1)}
            disabled={activeTab === experiences.length - 1}
            aria-label="Next role"
          >
            Next ›
          </button>
        </div>
      </div>
    </section>
  );
};

export default Experience;
